<template>
  <div class="comment-content">
    <RepliedComment
      v-if="repliedOnComment"
      :repliedOnComment="repliedOnComment"
      :originalComment="repliedOnOriginalComment"
      @goToRepliedComment="goToRepliedComment"
    />
    <div
      v-if="!editing && (hasText || demoVideoTimestamp)"
      class="comment-content__text"
    >
      <BaseSubHeading
        size="medium"
        weight="400"
      >
        <template v-if="demoVideoTimestamp">
          <CommentTimestampText
            :timestamp="demoVideoTimestamp"
            :clickable="!isDemoVideoDeleted"
            :history="isPreviousDemoVideo || isDemoVideoDeleted"
            :deleted="isDemoVideoDeleted"
            :label="demoVideoTimestampLabel"
            @timestampClick="emit('demoVideoTimestampSelect', $event)"
          />
          <ReferencedText
            v-if="hasText"
            :html="comment.structuredDescription.html || comment.structuredDescription.text"
          />
        </template>
        <ReferencedText
          v-else
          :html="comment.structuredDescription.html || comment.structuredDescription.text"
        />
      </BaseSubHeading>
    </div>
    <template v-if="editing">
      <div
        v-if="showDemoVideoTimestampEditor"
        class="comment-content__timestamp-edit"
      >
        <CommentTimestampInput
          :value="comment.demoVideoTimestamp"
          :enabled="comment.demoVideoTimestamp !== null && comment.demoVideoTimestamp >= 0"
          :syncEnabled="false"
          @change="emit('onEditDemoVideoTimestamp', $event)"
        />
      </div>
      <div class="upload-area__in custom-scroll upload-area__edit">
        <div class="upload-area__edit-row">
          <EditorInput
            id="comments"
            :value="editorValue"
            :jsonValue="pendingEditJson"
            :focusEditor="editing"
            :focusEnd="true"
            :triggerFocus="triggerFocus"
            :targetConfig="targetConfig"
            :referenceFileTargetConfig="referenceFileTargetConfig"
            :customExtensions="customExtensions"
            :referencesEnabled="referencesEnabled"
            @handleInput="onEditComment"
            @blur="onBlur"
          />
          <button
            v-if="!hideExpandButton"
            type="button"
            class="comment__expand-button"
            @click="$emit('expandEditor')"
          >
            <icon
              data="@icon/expand-open.svg"
              width="var(--icon-16)"
              height="var(--icon-16)"
            />
          </button>
        </div>
      </div>
    </template>
    <div
      v-if="filesOnly.length"
      class="comment-content__files"
    >
      <div
        v-for="(file, index) in filesOnly"
        :key="index"
        class="comment-content__file-wrapper"
        :class="{ 'is-deleted': file.deleted }"
      >
        <FileItem
          :file="file"
          :files="commentsFiles"
          :projectScoped="projectScoped"
          :progress="getProgressByFileId[file.id] ?? 0"
          :removeButton="editing"
          fancyboxGroup="comment_tab_fancybox"
          @remove="deleteFile"
        />
      </div>
    </div>
    <div
      v-if="editing && pendingEditFiles.length"
      class="comment-content__files"
    >
      <div
        v-for="(file, index) in pendingEditFiles"
        :key="'pending-' + index"
        class="comment-content__file-wrapper"
      >
        <FileItem
          :file="file"
          :removeButton="true"
          :projectScoped="projectScoped"
          @remove="removePendingFile"
        />
      </div>
    </div>
    <div
      v-if="editing && !hideControlsButtons"
      class="upload-area__edit-buttons comment-content__edit-buttons"
    >
      <button
        type="button"
        class="create-item__action-menu-item link is-medium-500 add-custom-outline links-divider__item"
        @click="$emit('onCancelEdit')"
      >
        Cancel
      </button>

      <button
        type="button"
        class="create-item__action-menu-item link is-medium-500 add-custom-outline"
        @click="onEnter"
      >
        Save
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue"

import CommentTimestampInput from "@/components/Comment/CommentTimestampInput.vue"
import CommentTimestampText from "@/components/Comment/CommentTimestampText.vue"
import RepliedComment from "@/components/Comment/RepliedComment.vue"
import EditorInput from "@/components/Editor/EditorInput.vue"
import FileItem from "@/components/File/FileItem.vue"
import { ReferencedText } from "@/components/Reference"

import {
  getDemoVideoVersionLabel,
  isDemoVideoIdDeleted,
  isPreviousDemoVideoId
} from "@/composables/useDemoVideo"
import { Comment, FileMetaData, TypeConfig, VUFile } from "@/models"
import { ReferenceAllUser } from "@/plugins/extensions/reference/referenceAt"
import { ReferenceHashFileLink } from "@/plugins/extensions/reference/referenceHash"
import { replaceHtmlTags } from "@/utils"
import { formatTimestamp } from "@/utils/timestampUtil"

import type { DemoVideoVersionInfo } from "@/composables/useDemoVideo"

const props = defineProps({
  comment: {
    type: Object as () => Comment,
    required: true
  },
  targetConfig: {
    type: Object as () => TypeConfig | null,
    default: null
  },
  referenceFileTargetConfig: {
    type: Object as () => TypeConfig | null,
    default: null
  },
  editing: {
    type: Boolean,
    default: false
  },
  pendingEditHtml: {
    type: String,
    default: ""
  },
  pendingEditJson: {
    type: String,
    default: ""
  },
  pendingEditFiles: {
    type: Array as () => VUFile[],
    default: () => []
  },
  commentOwner: {
    type: Boolean,
    default: false
  },
  triggerFocus: {
    type: Boolean,
    default: false
  },
  progress: {
    type: Object as () => { [key: string]: number },
    default: () => ({})
  },
  comments: {
    type: Array as () => Comment[],
    default: () => []
  },
  hideControlsButtons: {
    type: Boolean,
    default: false
  },
  hideExpandButton: {
    type: Boolean,
    default: false
  },
  showDemoVideoTimestampEditor: {
    type: Boolean,
    default: false
  },
  demoVideoVersionMap: {
    type: Object as () => Record<string, DemoVideoVersionInfo>,
    default: () => ({})
  },
  projectScoped: {
    type: Boolean,
    default: true
  },
  referencesEnabled: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits([
  "onBlur",
  "onEnter",
  "goToRepliedComment",
  "onEditComment",
  "deleteFile",
  "removePendingFile",
  "editComment",
  "expandEditor",
  "onCancelEdit",
  "onEditDemoVideoTimestamp",
  "demoVideoTimestampSelect"
])

const getProgressByFileId = computed(() => {
  return props.progress[props.comment.id] ?? {}
})

const customExtensions = computed(() => {
  return props.referencesEnabled && props.referenceFileTargetConfig
    ? [ReferenceAllUser, ReferenceHashFileLink]
    : []
})

const commentsFiles = computed(() => {
  return props.comments.reduce((acc: FileMetaData[], comment: Comment) => {
    if (comment.fileMetadata?.length) {
      acc = [...comment.fileMetadata, ...acc]
    }
    return acc
  }, [])
})

const filesOnly = computed(() => {
  return props.comment.fileMetadata?.filter(file => !file.isImage && !file.isVideo) ?? []
})

const repliedOnComment = computed(() => {
  return props.comment.repliedOnComment
})
const repliedOnOriginalComment = computed(() => {
  if (!props.comment.repliedOnComment?.id) return null

  return props.comments.find(comment => comment.id === props.comment.repliedOnComment?.id) ?? null
})

const demoVideoTimestamp = computed(() => {
  return props.comment.demoVideoTimestamp !== null && props.comment.demoVideoTimestamp >= 0
    ? formatTimestamp(props.comment.demoVideoTimestamp)
    : ""
})

const showDemoVideoTimestampEditor = computed(() => {
  return props.editing && props.showDemoVideoTimestampEditor
})

const isPreviousDemoVideo = computed(() =>
  isPreviousDemoVideoId(props.comment.demoVideoId, props.demoVideoVersionMap)
)
const isDemoVideoDeleted = computed(() =>
  isDemoVideoIdDeleted(props.comment.demoVideoId, props.demoVideoVersionMap)
)

const demoVideoTimestampLabel = computed(() => {
  if (!isPreviousDemoVideo.value && !isDemoVideoDeleted.value) return ""

  const versionLabel = getDemoVideoVersionLabel(
    props.comment.demoVideoId,
    props.demoVideoVersionMap
  )

  return isDemoVideoDeleted.value ? `${versionLabel} Deleted` : versionLabel
})

const hasText = computed(() => {
  const html = props.comment.structuredDescription?.html || ""
  return !!replaceHtmlTags(html).trim()
})

const editorValue = computed(() => {
  if (props.pendingEditHtml) {
    return props.pendingEditHtml
  }
  return props.comment.structuredDescription.html || props.comment.structuredDescription.text
})

function onBlur() {
  emit("onBlur")
}

function onEnter(event?: KeyboardEvent | MouseEvent) {
  if (event?.shiftKey || event?.ctrlKey) {
    return
  }

  emit("onEnter")
}

function goToRepliedComment() {
  if (!repliedOnComment.value || !repliedOnComment.value.id) {
    emit("goToRepliedComment", "")
    return
  }

  emit("goToRepliedComment", repliedOnComment.value.id ?? "")
}

function onEditComment(editorData) {
  emit("onEditComment", editorData)
}

function deleteFile(file) {
  emit("deleteFile", file)
}

function removePendingFile(file) {
  emit("removePendingFile", file)
}
</script>
